import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { STORE_URL } from '@insite-store360/shared';
import { Observable, catchError, map, of, shareReplay, tap } from 'rxjs';
import { ErrorHandlerService } from './error-handler.service';

/** One e-Warranty country, with the formats read from its own master record. */
export interface EwarrantyCountry {
  id: number;
  name: string;
  /** master.country.country_code, e.g. "+84". */
  phonePrefix: string;
  /** master.timezone.name via country.timezone_id. */
  timeZone: string;
  isPrimary?: boolean;
}

/** Setups & Controls > e-Warranty Settings, as the screens read them. */
export interface EwarrantySetting {
  countryIds: number[];
  primaryCountryId: number;
  countries: EwarrantyCountry[];
}

/** A channel the "Stores without Dealer" picker offers, from store_mdms.channel. */
export interface DealerChannelOption {
  id: number;
  name: string;
}

/**
 * The countries e-Warranty runs in. The screens used to decide this themselves
 * -- `/viet/i` on the Store, Add Store and Account pages, the literal id 8 on
 * Store Supplier Mapping, and a typed "+84" in the Customer and System User
 * forms.
 *
 * The GET is shared and replayed, so ten screens asking at once make one call,
 * and `refresh()` re-reads it after the settings page saves.
 */
@Injectable({ providedIn: 'root' })
export class EwarrantySettingService {
  private readonly API = `${STORE_URL}/ewarrantySetting`;

  /** Old behaviour, used until the first response lands or if it fails. */
  private static readonly FALLBACK: EwarrantySetting = {
    countryIds: [],
    primaryCountryId: 0,
    countries: [{ id: 0, name: 'Vietnam', phonePrefix: '+84', timeZone: 'Asia/Ho_Chi_Minh', isPrimary: true }],
  };

  private cached$?: Observable<EwarrantySetting>;
  /** Last value seen, for the synchronous checks in list components. */
  private current: EwarrantySetting = EwarrantySettingService.FALLBACK;

  constructor(private http: HttpClient, private errorHandler: ErrorHandlerService) {}

  get(): Observable<EwarrantySetting> {
    if (!this.cached$) {
      this.cached$ = this.http.get<any>(this.API).pipe(
        map((res) => this.normalize(res?.result ?? res)),
        tap((value) => (this.current = value)),
        catchError(() => of(EwarrantySettingService.FALLBACK)),
        shareReplay(1)
      );
    }
    return this.cached$;
  }

  /** The value already loaded. Components call `get()` once on init, then use this. */
  snapshot(): EwarrantySetting {
    return this.current;
  }

  /** True when `countryId` is one of the countries e-Warranty runs in. */
  isEwarrantyCountry(countryId: number | null | undefined): boolean {
    return !!countryId && this.current.countryIds.includes(Number(countryId));
  }

  /**
   * The country the all-country aggregates are bucketed on -- and the source of
   * the phone prefix the activation forms start from.
   */
  primaryCountry(): EwarrantyCountry {
    const { countries, primaryCountryId } = this.current;
    return (
      countries.find((c) => c.id === primaryCountryId) ??
      countries[0] ??
      EwarrantySettingService.FALLBACK.countries[0]
    );
  }

  /** The primary country's dialling prefix, for the phone fields. */
  phonePrefix(): string {
    return this.primaryCountry().phonePrefix;
  }

  /** The configured countries named for a message, e.g. "Vietnam / Thailand". */
  countryLabel(): string {
    return this.current.countries.map((c) => c.name).join(' / ');
  }

  refresh(): Observable<EwarrantySetting> {
    this.cached$ = undefined;
    return this.get();
  }

  /** Every active country, each with the prefix and zone from its master record. */
  countries(): Observable<EwarrantyCountry[]> {
    return this.http
      .get<any>(`${this.API}/countries`)
      .pipe(map((res) => res?.result ?? res ?? []), catchError(this.errorHandler.handleError));
  }

  update(setting: { countryIds: number[]; primaryCountryId: number }): Observable<EwarrantySetting> {
    return this.http.put<any>(this.API, setting).pipe(
      map((res) => this.normalize(res?.result ?? res)),
      tap((value) => {
        this.current = value;
        this.cached$ = undefined;
      }),
      catchError(this.errorHandler.handleError)
    );
  }

  /**
   * The "Stores without Dealer" qualifying-channel setting -- replaces the
   * hardcoded `GT`-only filter. Channel is not a per-country concept, so this
   * is a flat list rather than scoped like the countries above.
   */
  channels(): Observable<DealerChannelOption[]> {
    return this.http
      .get<any>(`${this.API}/channels`)
      .pipe(map((res) => res?.result ?? res ?? []), catchError(this.errorHandler.handleError));
  }

  /** The currently configured channel ids. Empty means "using the GT default". */
  dealerChannels(): Observable<number[]> {
    return this.http
      .get<any>(`${this.API}/dealer-channels`)
      .pipe(map((res) => (res?.result ?? res ?? []).map(Number)), catchError(this.errorHandler.handleError));
  }

  updateDealerChannels(channelIds: number[]): Observable<number[]> {
    return this.http
      .put<any>(`${this.API}/dealer-channels`, { channelIds })
      .pipe(map((res) => (res?.result ?? res ?? []).map(Number)), catchError(this.errorHandler.handleError));
  }

  /** The signed-in user's own e-Warranty language; null when never chosen. */
  myLanguage(): Observable<string | null> {
    return this.http.get<any>(`${this.API}/my-language`).pipe(
      map((res) => (res?.result ?? res)?.language ?? null),
      // A failed read must not block the screens; they fall back to English.
      catchError(() => of(null))
    );
  }

  updateMyLanguage(language: string): Observable<string> {
    return this.http
      .put<any>(`${this.API}/my-language`, { language })
      .pipe(map((res) => (res?.result ?? res)?.language), catchError(this.errorHandler.handleError));
  }

  private normalize(value: any): EwarrantySetting {
    const fallback = EwarrantySettingService.FALLBACK;
    const countries: EwarrantyCountry[] = (value?.countries ?? []).map((c: any) => ({
      id: Number(c?.id) || 0,
      name: c?.name || '',
      phonePrefix: c?.phonePrefix || fallback.countries[0].phonePrefix,
      timeZone: c?.timeZone || fallback.countries[0].timeZone,
      isPrimary: !!c?.isPrimary,
    }));
    if (!countries.length) {
      return fallback;
    }
    return {
      countryIds: (value?.countryIds ?? countries.map((c) => c.id)).map(Number),
      primaryCountryId:
        Number(value?.primaryCountryId) || countries.find((c) => c.isPrimary)?.id || countries[0].id,
      countries,
    };
  }
}
